import fileDownload from 'js-file-download';
import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import React, {useState} from 'react';
import type {SafetyEventSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {
  useExportSafetyEventsMutation,
  useGetSafetyEventListV2Query,
} from 'src/redux/queries/safetyEvent';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useSafetyEventColumns} from './hooks/useSafetyEventColumns';

export interface SafetyEventProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;

  actionBarPreNode?: React.ReactNode;
  actionBarPostNode?: React.ReactNode;

  onViewRowClick: (row: SafetyEventSimpleResponse) => void;

  filterAssetId?: number;
}

export const SafetyEventTable: React.FC<SafetyEventProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  actionBarPreNode,
  actionBarPostNode,
  onViewRowClick,
  filterAssetId,
}) => {
  const {setAlert} = useAlert();
  const {skipSafetyEvents} = useSkipTokensByFeature();
  const [isExporting, setIsExporting] = useState(false);
  const [exportSafetyEvents] = useExportSafetyEventsMutation();

  const columns = useSafetyEventColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'internalIdentifier', desc: true}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const params = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    filterAssetId: filterAssetId,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetSafetyEventListV2Query(params, {
      refetchOnMountOrArgChange: true,
      skip: skipSafetyEvents,
    });

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleExportDownload = () => {
    const fetch = async () => {
      setIsExporting(true);
      try {
        // Start/length are ignored, so we can use the same param request.
        const res = await exportSafetyEvents(params).unwrap();

        if (!res) {
          setAlert('Failed to export file.', 'error');
          return;
        }

        fileDownload(res.data, 'safety-event-export.csv');
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsExporting(false);
    };

    void fetch();
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {actionBarPreNode}
            <PermissionGuard
              module={ModuleEnum.SafetyEvent}
              operation={PermissionOperationEnum.Export}>
              <TableToolbar.Action
                variant="export"
                onClick={handleExportDownload}
                loading={isExporting}
              />
            </PermissionGuard>
            {actionBarPostNode}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
